import { useEffect, useState, useCallback } from "react";

// Per-shop cart persisted in localStorage. Each shop's cart is completely separate.
// Storage key: cpt.cart.{shopUsername} → JSON array of { product_id, title, image_url, unit_price_cents, quantity }

const key = (username) => `cpt.cart.${username}`;

const read = (username) => {
  try {
    const raw = localStorage.getItem(key(username));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const write = (username, items) => {
  try {
    if (!items || items.length === 0) {
      localStorage.removeItem(key(username));
    } else {
      localStorage.setItem(key(username), JSON.stringify(items));
    }
  } catch {}
  // Notify other useCart hooks in the same tab
  try {
    window.dispatchEvent(new CustomEvent("cpt-cart-change", { detail: { username } }));
  } catch {}
};

export function useCart(username) {
  const [items, setItems] = useState(() => (username ? read(username) : []));

  useEffect(() => {
    if (!username) return undefined;
    setItems(read(username));
    const onStorage = (e) => {
      if (e.key === key(username)) setItems(read(username));
    };
    const onLocal = (e) => {
      if (e.detail?.username === username) setItems(read(username));
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("cpt-cart-change", onLocal);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("cpt-cart-change", onLocal);
    };
  }, [username]);

  const addItem = useCallback((product) => {
    if (!username) return;
    const current = read(username);
    const existing = current.find((it) => it.product_id === product.product_id);
    let next;
    if (existing) {
      next = current.map((it) => it.product_id === product.product_id
        ? { ...it, quantity: Math.min(99, it.quantity + (product.quantity || 1)) }
        : it);
    } else {
      next = [...current, { ...product, quantity: product.quantity || 1 }];
    }
    write(username, next);
  }, [username]);

  const setQuantity = useCallback((product_id, qty) => {
    if (!username) return;
    const current = read(username);
    const nextQty = Math.max(0, Math.min(99, parseInt(qty, 10) || 0));
    const next = nextQty === 0
      ? current.filter((it) => it.product_id !== product_id)
      : current.map((it) => it.product_id === product_id ? { ...it, quantity: nextQty } : it);
    write(username, next);
  }, [username]);

  const removeItem = useCallback((product_id) => {
    if (!username) return;
    write(username, read(username).filter((it) => it.product_id !== product_id));
  }, [username]);

  const clear = useCallback(() => {
    if (!username) return;
    write(username, []);
  }, [username]);

  const subtotalCents = items.reduce((s, it) => s + (it.unit_price_cents || 0) * (it.quantity || 0), 0);
  const itemCount = items.reduce((s, it) => s + (it.quantity || 0), 0);

  return { items, addItem, setQuantity, removeItem, clear, subtotalCents, itemCount };
}
